import { FileText, Link2, Unlink, ExternalLink, Plus, Search, Loader2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  useLinkedDocuments,
  useLinkDocumentToIssue,
  useUnlinkDocumentFromIssue,
  useKnowledgeBaseSearch,
  type Document,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface LinkedDocumentsTabProps {
  issueId: string
  projectId: string
}

/**
 * 任务关联文档 Tab
 * 显示与任务关联的知识库文档列表
 */
export function LinkedDocumentsTab({ issueId, projectId }: LinkedDocumentsTabProps) {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)

  const { data: documents, isLoading, error } = useLinkedDocuments(issueId)
  const unlinkMutation = useUnlinkDocumentFromIssue()

  // 处理取消关联
  const handleUnlink = (documentId: string) => {
    unlinkMutation.mutate({ documentId, issueId })
  }

  // 处理跳转到文档
  const handleViewDocument = (doc: Document) => {
    window.location.href = `/knowledge-base/${doc.knowledgeBaseId}/documents/${doc.id}`
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    )
  }

  if (error) {
    return <div className="text-destructive py-8 text-center text-sm">加载关联文档失败</div>
  }

  return (
    <div className="space-y-4">
      {/* 头部操作栏 */}
      <div className="flex items-center justify-between">
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <FileText className="h-4 w-4" />
          <span>关联文档 ({documents?.length || 0})</span>
        </div>

        <LinkDocumentDialog
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
          issueId={issueId}
          projectId={projectId}
          linkedDocumentIds={documents?.map((d) => d.id) || []}
        />
      </div>

      {/* 文档列表 */}
      {documents && documents.length > 0 ? (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="hover:bg-muted/50 group flex items-center justify-between rounded-lg border p-3 transition-colors"
            >
              <div className="flex min-w-0 items-center gap-3">
                <FileText className="h-5 w-5 shrink-0 text-blue-500" />
                <div className="min-w-0">
                  <button
                    onClick={() => handleViewDocument(doc)}
                    className="text-foreground hover:text-primary block truncate text-sm font-medium hover:underline"
                  >
                    {doc.title}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleViewDocument(doc)}
                  title="查看文档"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleUnlink(doc.id)}
                  disabled={unlinkMutation.isPending}
                  title="取消关联"
                  className="text-destructive hover:text-destructive"
                >
                  <Unlink className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-8">
          <Link2 className="text-muted-foreground/50 h-8 w-8" />
          <p className="text-muted-foreground text-sm">暂无关联文档</p>
          <Button variant="outline" size="sm" onClick={() => setLinkDialogOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            关联文档
          </Button>
        </div>
      )}
    </div>
  )
}

// ==================== 文档关联对话框 ====================

interface LinkDocumentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  issueId: string
  projectId: string
  linkedDocumentIds: string[]
}

function LinkDocumentDialog({
  open,
  onOpenChange,
  issueId,
  linkedDocumentIds,
}: LinkDocumentDialogProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const { data: searchResults, isLoading: isSearching } = useKnowledgeBaseSearch(searchQuery)
  const linkMutation = useLinkDocumentToIssue()

  // 处理关联
  const handleLink = (documentId: string) => {
    linkMutation.mutate(
      { documentId, issueId },
      {
        onSuccess: () => {
          // 关联成功后不关闭对话框，允许继续关联
        },
      }
    )
  }

  // 判断文档是否已关联
  const isLinked = (documentId: string) => linkedDocumentIds.includes(documentId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="mr-1 h-4 w-4" />
          关联文档
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>关联知识库文档</DialogTitle>
          <DialogDescription>搜索并选择要关联到此任务的文档</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* 搜索框 */}
          <div className="relative">
            <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="搜索文档..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* 搜索结果 */}
          <div className="max-h-64 overflow-y-auto">
            {searchQuery.length < 2 ? (
              <div className="text-muted-foreground py-8 text-center text-sm">
                请输入至少 2 个字符进行搜索
              </div>
            ) : isSearching ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
              </div>
            ) : searchResults && searchResults.data.length > 0 ? (
              <div className="space-y-1">
                {searchResults.data.map((result) => {
                  const linked = isLinked(result.id)
                  return (
                    <button
                      key={result.id}
                      onClick={() => !linked && handleLink(result.id)}
                      disabled={linked || linkMutation.isPending}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors',
                        linked ? 'bg-muted/50 cursor-not-allowed opacity-60' : 'hover:bg-muted'
                      )}
                    >
                      <FileText className="h-4 w-4 shrink-0 text-blue-500" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{result.title}</p>
                      </div>
                      {linked && (
                        <span className="text-muted-foreground shrink-0 text-xs">已关联</span>
                      )}
                    </button>
                  )
                })}
              </div>
            ) : (
              <div className="text-muted-foreground py-8 text-center text-sm">未找到匹配的文档</div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
